import { Button, Card, Col, Image, Modal, Row, Spinner, Stack } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import OTPInput from "react-otp-input"
import useMfaSetup from "@views/Settings/useMfaSetup.ts"
import ReauthenticationForm from "@components/Settings/ReauthenticationForm.tsx"

export default function MultiFactorSetupModal() {
	const {
		onNextStep,
		onPasswordChange,
		onOtpCodeChange,
		downloadRecoveryCodes,
		getNextStepButtonLabel,
		otpCode,
		password,
		qrCode,
		secret,
		isLoading,
		isLastStep,
		step,
		recoveryCodes,
	} = useMfaSetup()

	const { t } = useTranslation()
	const navigate = useNavigate()

	function onPrevStep() {
		navigate("/settings")
	}

	const formSteps = [
		<ReauthenticationForm key="password-form" onPasswordChange={onPasswordChange} password={password} />,
		<SetupAuthenticator key="setup-authenticator" />,
		<AuthenticatorQrCode key="authenticator-qr-code" qrCode={qrCode || ""} secret={secret || ""} />,
		<EnableAuthenticator key="enable-authenticator" onOtpCodeChange={onOtpCodeChange} otpCode={otpCode} />,
		<DownloadRecoveryCodes
			key="download-recovery-codes"
			onDownloadRecoveryCodes={downloadRecoveryCodes}
			recoveryCodes={recoveryCodes ?? []}
		/>,
	]

	const handleClose = () => (isLastStep ? null : navigate("/settings"))

	return (
		<Modal show={true} onHide={handleClose} size="lg" centered>
			<Stack className="p-5 " direction="vertical">
				{formSteps[step]}

				<Stack className="justify-content-end pt-4" direction="horizontal" gap={2}>
					{!isLastStep && (
						<Button variant="secondary" onClick={onPrevStep}>
							{t("Peruuta")}
						</Button>
					)}
					<Button variant="primary" onClick={onNextStep}>
						{isLoading ? <Spinner as="span" animation="border" size="sm" /> : getNextStepButtonLabel()}
					</Button>
				</Stack>
			</Stack>
		</Modal>
	)
}

function SetupAuthenticator() {
	const { t } = useTranslation()
	return (
		<Stack>
			<h2>{t("Ota monivaiheinen tunnistautuminen käyttöön")}</h2>

			<p className="mt-2">
				{t(
					"Kun otat monivaiheisen tunnistautumisen käyttöön, pyydämme kirjautumisen yhteydessä sinua vahvistamaan henkilöllisyytesi salasanan lisäksi kertakäyttöisellä koodilla. Saat koodin tunnuslukusovelluksesta (esim. Microsoft Authenticator). Tämä estää käyttäjätilisi väärinkäyttöä, lisäämällä vahvan lisäsuojan pelkän salasanan lisäksi."
				)}
			</p>
		</Stack>
	)
}

interface AuthenticatorQrCodeProps {
	qrCode: string
	secret: string
}

function AuthenticatorQrCode({ qrCode, secret }: AuthenticatorQrCodeProps) {
	const { t } = useTranslation()
	const [showSecret, setShowSecret] = useState(false)

	return (
		<Card className="mt-4">
			<Stack className=" align-items-center" direction="horizontal" gap={2}>
				<Image src={qrCode} height={240} width={240} className="p-2 border border-light" />
				<Stack direction="vertical" className="justify-content-center align-items-start">
					<p className="mb-0 fw-bold">{t("Skannaa QR-koodi tunnuslukusovelluksellasi.")}</p>
					{qrCode && !showSecret && (
						<Button variant="link" onClick={() => setShowSecret(true)} className="p-0">
							{t("Etkö voi skannata koodia?")}
						</Button>
					)}
					{showSecret && secret && (
						<p className="mt-2">
							{t("Kirjoita tämä käyttöönottoavain tunnuslukusovellukseesi")}: <code>{secret}</code>
						</p>
					)}
				</Stack>
			</Stack>
		</Card>
	)
}

interface EnableAuthenticatorProps {
	otpCode: string
	onOtpCodeChange: (otpCode: string) => void
}

function EnableAuthenticator({ otpCode, onOtpCodeChange }: EnableAuthenticatorProps) {
	const { t } = useTranslation()

	return (
		<Stack>
			<h2>{t("Anna väliaikainen koodi")}</h2>
			<p className="mt-2">{t("Kirjoita kuusi numeroinen koodi, joka lukee tunnuslukusovelluksessasi.")}</p>
			<Stack direction="horizontal" className="mt-2 justify-content-center align-items-center">
				<OTPInput
					value={otpCode}
					onChange={onOtpCodeChange}
					numInputs={6}
					inputType="text"
					containerStyle={{ gap: 8 }}
					renderInput={(props) => (
						<input {...props} className="form-control text-center fs-4" style={{ height: 60, width: 60 }} />
					)}
				/>
			</Stack>
		</Stack>
	)
}

function DownloadRecoveryCodes({
	onDownloadRecoveryCodes,
	recoveryCodes,
}: {
	onDownloadRecoveryCodes: () => void
	recoveryCodes: string[]
}) {
	const { t } = useTranslation()

	function getDisplayRecoveryCode(code: string) {
		return (
			<>
				<span>{code.substring(0, 4)}</span>
				<span className="user-select-none">-</span>
				<span>{code.substring(4, 8)}</span>
			</>
		)
	}

	return (
		<Stack>
			<h2>{t("Tallenna tilisi palautuskoodit")}</h2>
			<p className="mt-2">
				{t(
					"Palautuskoodit ovat tilisi vara-avain. Jos menetät pääsyn tunnuslukusovellukseesi voit käyttää palautuskoodia kirjautuaksesi sisään."
				)}{" "}
				<strong>{t("Näet palautuskoodit vain kerran.")}</strong>
			</p>
			<Card>
				<Row className="mb-3 rounded">
					<Col md={6} className="p-3">
						{recoveryCodes.slice(0, 5).map((code) => (
							<p key={code} className="text-center my-3">
								{getDisplayRecoveryCode(code)}
							</p>
						))}
					</Col>
					<Col md={6} className="p-3">
						{recoveryCodes.slice(5, 10).map((code) => (
							<p key={code} className="text-center my-3">
								{getDisplayRecoveryCode(code)}
							</p>
						))}
					</Col>
				</Row>
			</Card>
			<Button onClick={onDownloadRecoveryCodes}>{t("Lataa palautuskoodit")}</Button>
		</Stack>
	)
}
